import { ApolloError, useQuery } from '@apollo/client'
import { ReactNode, useEffect, useRef, useState } from 'react'
import ErrorPage, { ErrorType } from '../../../controls/ErrorPage'
import { ProjectType } from '../../../data/gql-gen/dashboard/graphql'
import {
  DraftQuestionnaireIdQuery,
  DraftQuestionnaireIdQueryVariables
} from '../../../data/gql-gen/questionnaire/graphql'
import { DRAFT_QUESTIONNAIRE_ID } from '../../../hooks/localState/useDraftQuestionnaireIdCache'
import { useCreateInitialQuestionnaire } from '../../../hooks/project/useCreateInitialQuestionnaire'
import useGetUserAndAccount from '../../../hooks/useGetUserAndAccount'
import { useProjectId } from '../../../hooks/useProjectId'
import { surveysByCreation } from '../MultiMarket/markets.utils'
import { useWaveId } from './useWaveId'
import { useWaves } from './useWaves'

const isQuestionnaireNotFound = (error?: ApolloError): boolean =>
  error?.graphQLErrors.some((graphQLError) =>
    /questionnaire not found/i.test(graphQLError.message)
  ) ?? false

interface WaveSetupProps {
  projectId: string
  waveId: string
  surveyId: string
  onDone: () => void
}

// Mounted only while setup is needed, so the account (for its markup) is read
// only then.
const WaveSetup = ({ projectId, waveId, surveyId, onDone }: WaveSetupProps) => {
  const { account, markup } = useGetUserAndAccount()
  const { createInitialQuestionnaire } = useCreateInitialQuestionnaire()
  const [failed, setFailed] = useState(false)
  const started = useRef(false)

  useEffect(() => {
    // `account` is set once the account, and so its markup, has loaded.
    if (!account || started.current) {
      return
    }
    started.current = true

    createInitialQuestionnaire({
      projectId,
      surveyId,
      waveId,
      projectType: ProjectType.Tracker,
      accountMarkup: markup
    }).then((created) => (created ? onDone() : setFailed(true)))
  }, [
    account,
    markup,
    createInitialQuestionnaire,
    projectId,
    surveyId,
    waveId,
    onDone
  ])

  return failed ? <ErrorPage errorType={ErrorType.GenericError} /> : null
}

/**
 * Every wave step reads and edits the wave's own questionnaire. createWave
 * leaves the first wave without one, so it is created here for the wave's
 * first market, with that market's default fieldwork, the first time the wave
 * is opened, and again on a later visit if the wave still has none. Later
 * waves are copies and already have one.
 */
export const WaveQuestionnaireGuard = ({
  children
}: {
  children: ReactNode
}) => {
  const projectId = useProjectId()
  const waveId = useWaveId()
  const { waves, loading: loadingWaves } = useWaves(projectId)
  const firstSurveyId = surveysByCreation(
    waves.find((wave) => wave.waveId === waveId)?.surveys
  ).at(0)?.surveyId

  const questionnaire = useQuery<
    DraftQuestionnaireIdQuery,
    DraftQuestionnaireIdQueryVariables
  >(DRAFT_QUESTIONNAIRE_ID, {
    variables: { projectId, waveId },
    context: { clientName: 'questionnaire' },
    fetchPolicy: 'no-cache',
    errorPolicy: 'all'
  })
  const questionnaireMissing =
    !questionnaire.loading &&
    !questionnaire.data?.draftQuestionnaire &&
    isQuestionnaireNotFound(questionnaire.error)

  if (questionnaireMissing && firstSurveyId) {
    return (
      <WaveSetup
        projectId={projectId}
        waveId={waveId}
        surveyId={firstSurveyId}
        onDone={() => questionnaire.refetch()}
      />
    )
  }

  if (questionnaire.loading || (questionnaireMissing && loadingWaves)) {
    return null
  }

  return <>{children}</>
}
